// src/pages/CategoryPage.tsx
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbLd, itemListLd } from '../components/common/SEO';
import Hero from '../components/common/Hero';
import ChipFilter from '../components/common/ChipFilter';
import Breadcrumb from '../components/common/Breadcrumb';
import SearchBar from '../components/common/SearchBar';
import ProgramCard from '../components/common/ProgramCard';
import Sidebar from '../components/common/Sidebar';
import InContentAd from '../components/ads/InContentAd';
import Icon from '../components/common/Icon';
import { getCategory } from '../data/categories';
import { searchPrograms, getProgramsByCategory } from '../data/supportPrograms';
import { changes2026 } from '../data/changes2026';
import { getOutlookByCategory } from '../data/outlook2027';
import { CategoryId } from '../types';

interface CategoryPageProps {
    categoryId: CategoryId;
    /** 히어로 일러스트 종류 */
    art?: 'documents' | 'chart' | 'house' | 'family';
}

const CategoryPage: React.FC<CategoryPageProps> = ({ categoryId, art = 'documents' }) => {
    const [term, setTerm] = useState('');
    const cat = getCategory(categoryId);

    const all = useMemo(() => getProgramsByCategory(categoryId), [categoryId]);
    const programs = useMemo(() => searchPrograms(term, categoryId), [term, categoryId]);

    const catChanges = changes2026.filter(c => c.category === categoryId);
    const catOutlook = getOutlookByCategory(categoryId);

    const crumbs = [
        { name: '홈', path: '/' },
        { name: cat.name, path: cat.path },
    ];

    /* 히어로 지표: 짧고 숫자가 드러나는 대표 금액만 골라 3개 */
    const stats = all
        .map(p => ({
            value: p.amount.split(' · ')[0].replace(/\s*\(.*$/, '').trim(),
            label: p.title,
        }))
        .filter(s => /\d/.test(s.value) && s.value.length <= 18)
        .slice(0, 3);

    return (
        <>
            <SEO
                path={cat.path}
                title={`2026년 ${cat.name} 총정리 - 자격요건·금액·신청방법`}
                description={`${cat.description} 2026년 기준 ${all.length}개 제도의 지원금액과 신청 방법, 2027년 전망까지 정리했습니다.`}
                keywords={cat.keywords}
                updated="2026-09-02"
                jsonLd={[
                    breadcrumbLd(crumbs),
                    itemListLd(
                        `2026년 ${cat.name}`,
                        all.map(p => ({ title: p.title, path: `/program/${p.slug}` }))
                    ),
                ]}
            />

            <div className="gh-layout">
                <div>
                    <Breadcrumb items={crumbs} />

                    <Hero
                        eyebrow={`2026년 기준 ${all.length}개 제도`}
                        eyebrowIcon={cat.icon}
                        title={cat.name}
                        description={cat.description}
                        stats={stats}
                        art={art}
                    />

                    <ChipFilter />

                    <SearchBar
                        searchTerm={term}
                        setSearchTerm={setTerm}
                        placeholder={`${cat.shortName} 지원금 검색 (예: ${all[0]?.title ?? '지원금'})`}
                    />

                    <section className="gh-sec">
                        <div className="gh-sec__head">
                            <div>
                                <h2 className="gh-sec__title">
                                    <Icon name={cat.icon} size={19} />
                                    {cat.name} 전체
                                </h2>
                                <p className="gh-sec__desc">{cat.tagline}</p>
                            </div>
                            <span className="gh-count">
                                총 <b>{programs.length}</b>건
                            </span>
                        </div>

                        {programs.length === 0 ? (
                            <div className="gh-empty">
                                <Icon name="search" size={40} />
                                <p className="gh-empty__title">검색 결과가 없습니다</p>
                                <p className="gh-empty__desc">
                                    다른 키워드로 검색하거나 검색어를 지우고 전체 목록을 확인해 보세요.
                                </p>
                            </div>
                        ) : (
                            <div className="gh-cards">
                                {programs.map(p => (
                                    <ProgramCard key={p.id} program={p} hideCategory />
                                ))}
                            </div>
                        )}
                    </section>

                    <InContentAd />

                    {catChanges.length > 0 && (
                        <section className="gh-sec">
                            <div className="gh-sec__head">
                                <div>
                                    <h2 className="gh-sec__title">
                                        <Icon name="sparkle" size={19} />
                                        2026년 {cat.shortName} 분야 변경 사항
                                    </h2>
                                </div>
                                <Link to="/2026" className="gh-sec__more">
                                    전체 보기
                                    <Icon name="chevronRight" size={13} strokeWidth={2.4} />
                                </Link>
                            </div>

                            <div className="gh-ba">
                                {catChanges.map(c => (
                                    <article className="gh-ba__item" key={c.id}>
                                        <h3 className="gh-ba__title">{c.title}</h3>
                                        <div className="gh-ba__row">
                                            <div className="gh-ba__cell gh-ba__cell--before">
                                                <b>2025년</b>
                                                {c.before}
                                            </div>
                                            <Icon name="arrowRight" size={18} className="gh-ba__arrow" />
                                            <div className="gh-ba__cell gh-ba__cell--after">
                                                <b>2026년</b>
                                                {c.after}
                                            </div>
                                        </div>
                                        <p className="gh-ba__detail">{c.detail}</p>
                                    </article>
                                ))}
                            </div>
                        </section>
                    )}

                    {catOutlook.length > 0 && (
                        <section className="gh-sec">
                            <div className="gh-sec__head">
                                <div>
                                    <h2 className="gh-sec__title">
                                        <Icon name="chart" size={19} />
                                        2027년 {cat.shortName} 분야 <em>전망</em>
                                    </h2>
                                    <p className="gh-sec__desc">2026.09.01 발표 정부 예산안 기준</p>
                                </div>
                                <Link to="/2027" className="gh-sec__more">
                                    전체 보기
                                    <Icon name="chevronRight" size={13} strokeWidth={2.4} />
                                </Link>
                            </div>

                            <div className="gh-outlook">
                                {catOutlook.map(o => (
                                    <article className="gh-outlook__item" key={o.id}>
                                        <div className="gh-outlook__figure">
                                            <div className="gh-outlook__fig">{o.figure}</div>
                                            <div className="gh-outlook__figlabel">{o.figureLabel}</div>
                                        </div>
                                        <div className="gh-outlook__main">
                                            <h3 className="gh-outlook__title">
                                                {o.title}
                                                <span className={`gh-status gh-status--${o.status}`}>
                                                    {o.status}
                                                </span>
                                            </h3>
                                            <p className="gh-outlook__detail">{o.detail}</p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </section>
                    )}

                </div>

                <Sidebar category={categoryId} rankTitle={`${cat.shortName} 인기 지원금`} />
            </div>
        </>
    );
};

export default CategoryPage;
